import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Resource, ResourceService } from '@klickdata/core/resource';
import { Filter, GlobalFilterProperty, SelectFilterOption, TableFilterComponent } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    catchError,
    combineLatest,
    exhaustMap,
    map,
    merge,
    startWith,
    switchMap,
    tap,
} from 'rxjs';

@Component({
    selector: 'app-resources-sub-grid-base',
    templateUrl: './resources-sub-grid-base.component.html',
    styleUrls: ['./resources-sub-grid-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourcesSubGridBaseComponent implements AfterViewInit {
    @Input() tableFilterItems: Filter<any>[];
    @Input() showOccasion: boolean;
    @Input() guestMode: boolean;
    @Input() hasThumbnailHoverEffect: boolean;
    @Input() label: string;
    @Input() resBlockWidth: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    @Input() lastPaginatorPage: PageEvent;
    @Input() pageEvent: Subject<PageEvent>;
    // Replaces the paginator with "load the next page when the list end scrolls into view".
    // Read once when the grid is created, so the caller must not toggle it afterwards.
    @Input() loadMoreOnScroll: boolean;
    public pageSizeOptions: number[] = [];
    @ViewChild(MatPaginator) paginator: MatPaginator;
    GlobalFilterProperty = GlobalFilterProperty;
    public appliedFilters: Subject<Filter<any>[]> = new Subject<Filter<any>[]>();
    public sectionSortingOptions: SelectFilterOption[];
    public resources$: Observable<Resource[]>;
    @Output() onPaginatorPageChange: EventEmitter<{ pageSize: number; pageIndex: number }> = new EventEmitter<{
        pageSize: number;
        pageIndex: number;
    }>();

    expanded = false;
    public isLoadingNextPage = false;
    public canLoadNextPage = false;
    private nextPageRequest: Subject<void> = new Subject<void>();
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    constructor(protected resourceService: ResourceService, protected cdr: ChangeDetectorRef) {
        this.sectionSortingOptions = Utils.getSectionsSortingOptions();
    }

    ngAfterViewInit(): void {
        this.resources$ = this.loadMoreOnScroll ? this.getResourcesLoadedOnScroll() : this.getPaginatedResources();

        setTimeout(() => {
            this.expanded = this.filter.hasActiveFilters();
            this.cdr.markForCheck();
        }, 1000);
    }

    public loadNextPage() {
        if (this.canLoadNextPage) {
            this.nextPageRequest.next();
        }
    }

    private getPaginatedResources(): Observable<Resource[]> {
        return combineLatest([
            merge(this.pageEvent.asObservable(), this.paginator.page),
            this.appliedFilters.asObservable().pipe(startWith([])),
        ]).pipe(
            switchMap(([pageEvent, filters]) => {
                this.lastPaginatorPage = pageEvent;
                return this.fetchResources(filters, pageEvent.pageIndex, pageEvent.pageSize);
            }),
            tap((response) => {
                this.lastPaginatorPage.length = response.total_count;
                this.updatePaginator(this.lastPaginatorPage);
            }),
            map((response) => response.data),
            tap(() => setTimeout(() => this.cdr.detectChanges()))
        );
    }

    private getResourcesLoadedOnScroll(): Observable<Resource[]> {
        return combineLatest([
            this.pageEvent.asObservable(),
            this.appliedFilters.asObservable().pipe(startWith([])),
        ]).pipe(
            // A new page size (screen resize) or new filters start the list again from the first page.
            switchMap(([pageEvent, filters]) => {
                let nextPageIndex = 0;
                let loadedResources: Resource[] = [];
                return this.nextPageRequest.pipe(
                    startWith(null),
                    // Next page requests that arrive while a page is still loading are ignored.
                    exhaustMap(() => {
                        this.canLoadNextPage = false;
                        this.isLoadingNextPage = nextPageIndex > 0;
                        return this.fetchResources(filters, nextPageIndex, pageEvent.pageSize).pipe(
                            // Keep the loaded items and stop the spinner; loading resumes on new filters or page size.
                            catchError(() => {
                                this.isLoadingNextPage = false;
                                this.cdr.markForCheck();
                                return EMPTY;
                            })
                        );
                    }),
                    map((response) => {
                        nextPageIndex++;
                        loadedResources = [...loadedResources, ...response.data];
                        this.canLoadNextPage =
                            response.data.length > 0 && loadedResources.length < response.total_count;
                        this.isLoadingNextPage = false;
                        return loadedResources;
                    })
                );
            })
        );
    }

    private fetchResources(
        filters: Filter<any>[],
        pageIndex: number,
        pageSize: number
    ): Observable<{ data: Resource[]; total_count: number }> {
        const pageFilters = [...filters, ...this.tableFilterItems, { property: 'page', items: [pageIndex] }];
        return this.guestMode
            ? this.resourceService.getGuestResourcesByFilters(pageFilters, pageSize)
            : this.resourceService.getResourcesByFilters(pageFilters, pageSize);
    }

    private updatePaginator(page: PageEvent) {
        this.paginator.length = page.length;
        this.paginator.pageIndex = page.pageIndex;
        this.paginator.pageSize = page.pageSize;

        // Highest choice is the section size when it is under 100, otherwise 100.
        if (this.pageSizeOptions.length == 0 && page.length > 0) {
            const maxItems = page.length < 100 ? page.length : 100;
            const options: number[] = [];
            for (let i = 1; i <= 3; i++) {
                const sizeOption = page.pageSize * i;
                if (sizeOption < page.length && sizeOption < maxItems) {
                    options.push(sizeOption);
                }
            }
            options.push(maxItems);
            this.pageSizeOptions = options;
            if (this.paginator.pageSize > maxItems) {
                this.paginator.pageSize = maxItems;
            }
        }
        // #161 Hack to fix when current paginator page more then total pages paginator doesn't make correction by default.
        // https://github.com/angular/material2/issues/5812
        // https://github.com/angular/material2/issues/10227
        if (this.paginator.pageIndex !== 0 && this.paginator.pageIndex >= this.paginator.getNumberOfPages()) {
            this.paginator.previousPage();
        }
        this.cdr.detectChanges();
    }
}
